import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { PageHeader } from '../../../components/ui/PageHeader'
import { Spinner } from '../../../components/ui/Spinner'
import { Alert } from '../../../components/ui/Alert'
import { Modal } from '../../../components/ui/Modal'
import { inputClass, selectClass } from '../../../components/ui/FormField'
import { api } from '../../../api/axios'
import { useAuth } from '../../../contexts/AuthContext'
import { useAtiradores } from '../../atiradores/hooks/useAtiradores'
import { DataBR } from '../../../components/ui/DataBR'

interface Arma {
  idarma: number
  idatirador: number
  marca: string
  modelo: string
  calibre: string
  tipo: string
  numero_serie?: string | null
  numero_sigma?: string | null
  registro_sigma_sinarm?: string | null
  cr_arma?: string | null
  cr_arma_validade?: string | null
  inciso?: string | null
}

const TIPOS = ['Pistola', 'Revolver', 'Carabina', 'Espingarda', 'Rifle', 'Outro']
const INCISOS = [
  'Inciso II, art. 11, Dec 11.615/2023',
  'Inciso III, art. 12, Dec 11.615/2023',
  'Inciso IV, art. 12, Dec 11.615/2023',
]

export function MinhasArmasPage() {
  const { hasRole } = useAuth()
  const isAdmin = hasRole('admin')
  const qc = useQueryClient()

  // Selecao de atirador (admin)
  const [selAtirador, setSelAtirador] = useState<number | ''>('')
  const [searchAtirador, setSearchAtirador] = useState('')
  const { data: atiradoresData } = useAtiradores(
    isAdmin && searchAtirador.length >= 3 ? { nome: searchAtirador, limit: 20 } : {},
  )
  const atiradores = atiradoresData?.data ?? []

  const queryParams = isAdmin && selAtirador ? `?idatirador=${selAtirador}` : ''

  const { data, isLoading } = useQuery({
    queryKey: ['minhas-armas', selAtirador],
    queryFn: async () => {
      const resp = await api.get<{ data: Arma[]; idatirador?: number }>(`/publico/minhas-armas${queryParams}`)
      return resp.data
    },
    enabled: !isAdmin || !!selAtirador,
  })

  const armas = data?.data ?? []

  // Form
  const [editing, setEditing] = useState<Arma | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<Partial<Arma>>({})
  const [alert, setAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  function openCreate() {
    setEditing(null)
    setForm({ tipo: 'Pistola' })
    setShowForm(true)
  }
  function openEdit(arma: Arma) {
    setEditing(arma)
    setForm({ ...arma })
    setShowForm(true)
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload = { ...form }
      if (isAdmin && selAtirador && !editing) (payload as any).idatirador = selAtirador
      if (editing) {
        return api.put(`/publico/minhas-armas/${editing.idarma}`, payload).then(r => r.data.data)
      }
      return api.post('/publico/minhas-armas', payload).then(r => r.data.data)
    },
    onSuccess: () => {
      setAlert({ type: 'success', message: editing ? 'Arma atualizada!' : 'Arma cadastrada!' })
      setShowForm(false)
      qc.invalidateQueries({ queryKey: ['minhas-armas'] })
    },
    onError: (err: any) => {
      setAlert({ type: 'error', message: err?.response?.data?.error || 'Erro ao salvar arma' })
    },
  })

  return (
    <div className="space-y-4">
      <PageHeader title="Minhas Armas" description="Cadastre suas armas para vincular as inscricoes e gerar declaracoes" />
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      {isAdmin && (
        <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 p-4 shadow-sm space-y-3">
          <h3 className="font-semibold text-compumaq-texto">Selecionar Atirador</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text" className={inputClass}
              placeholder="Buscar por nome (3+ letras)..."
              value={searchAtirador}
              onChange={e => setSearchAtirador(e.target.value)}
            />
            <select
              className={selectClass}
              value={selAtirador}
              onChange={e => setSelAtirador(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">Selecione...</option>
              {atiradores.map(a => (
                <option key={a.idatirador} value={a.idatirador}>{a.nome} — {a.cpf}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {(!isAdmin || selAtirador) && (
        <div className="flex justify-end">
          <button onClick={openCreate}
            className="px-4 py-2 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-semibold hover:bg-compumaq-doradoClaro">
            + Nova Arma
          </button>
        </div>
      )}

      <div className="bg-compumaq-pretoSuave rounded-xl border border-compumaq-dourado/25 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-12 flex justify-center"><Spinner size="lg" /></div>
        ) : armas.length === 0 ? (
          <div className="p-12 text-center text-compumaq-textoSuave">
            {isAdmin && !selAtirador ? 'Selecione um atirador acima.' : 'Nenhuma arma cadastrada.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-compumaq-preto text-xs">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Marca/Modelo</th>
                  <th className="text-left px-3 py-2 font-medium">Calibre</th>
                  <th className="text-left px-3 py-2 font-medium">Tipo</th>
                  <th className="text-left px-3 py-2 font-medium">N. Serie</th>
                  <th className="text-left px-3 py-2 font-medium">SIGMA</th>
                  <th className="text-left px-3 py-2 font-medium">Reg. SIGMA/SINARM</th>
                  <th className="text-left px-3 py-2 font-medium">CR / Validade</th>
                  <th className="text-right px-3 py-2 font-medium">Acoes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-compumaq-dourado/25">
                {armas.map(arma => (
                  <tr key={arma.idarma} className="hover:bg-compumaq-preto">
                    <td className="px-3 py-2"><span className="font-medium">{arma.marca}</span> {arma.modelo}</td>
                    <td className="px-3 py-2">{arma.calibre}</td>
                    <td className="px-3 py-2">{arma.tipo}</td>
                    <td className="px-3 py-2 text-compumaq-textoSuave">{arma.numero_serie || '—'}</td>
                    <td className="px-3 py-2 text-compumaq-textoSuave">{arma.numero_sigma || '—'}</td>
                    <td className="px-3 py-2 text-compumaq-textoSuave">{arma.registro_sigma_sinarm || '—'}</td>
                    <td className="px-3 py-2 text-compumaq-textoSuave text-xs">
                      {arma.cr_arma || '—'}
                      {arma.cr_arma_validade && <div>val: {new Date(arma.cr_arma_validade).toLocaleDateString('pt-BR')}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(arma)} className="text-xs text-compumaq-doradoClaro hover:text-compumaq-doradoClaro font-medium">
                        Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title={editing ? 'Editar Arma' : 'Nova Arma'} size="md">
        <form onSubmit={e => { e.preventDefault(); saveMut.mutate() }} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Marca *" required>
              <input className={inputClass} value={form.marca ?? ''} onChange={e => setForm({ ...form, marca: e.target.value })} required />
            </Field>
            <Field label="Modelo *" required>
              <input className={inputClass} value={form.modelo ?? ''} onChange={e => setForm({ ...form, modelo: e.target.value })} required />
            </Field>
            <Field label="Calibre *" required>
              <input className={inputClass} value={form.calibre ?? ''} onChange={e => setForm({ ...form, calibre: e.target.value })} placeholder=".38SPL, 9MM, .22LR..." required />
            </Field>
            <Field label="Tipo *" required>
              <select className={selectClass} value={form.tipo ?? 'Pistola'} onChange={e => setForm({ ...form, tipo: e.target.value })} required>
                {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Numero de Serie">
              <input className={inputClass} value={form.numero_serie ?? ''} onChange={e => setForm({ ...form, numero_serie: e.target.value })} />
            </Field>
            <Field label="Numero SIGMA">
              <input className={inputClass} value={form.numero_sigma ?? ''} onChange={e => setForm({ ...form, numero_sigma: e.target.value })} />
            </Field>
            <Field label="Registro SIGMA/SINARM">
              <input className={inputClass} value={form.registro_sigma_sinarm ?? ''} onChange={e => setForm({ ...form, registro_sigma_sinarm: e.target.value })} placeholder="Ex: 123456789" />
            </Field>
            <Field label="CR (Cert. Registro)">
              <input className={inputClass} value={form.cr_arma ?? ''} onChange={e => setForm({ ...form, cr_arma: e.target.value })} />
            </Field>
            <Field label="Validade do CR">
              <DataBR className={inputClass} value={form.cr_arma_validade ?? ''} onChange={e => setForm({ ...form, cr_arma_validade: e.target.value })} />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Inciso da legislacao (Decreto 11.615/2023)">
                <select className={selectClass} value={form.inciso ?? ''} onChange={e => setForm({ ...form, inciso: e.target.value })}>
                  <option value="">— nao informado —</option>
                  {INCISOS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </Field>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 py-2.5 border border-compumaq-dourado/25 rounded-lg text-sm">
              Cancelar
            </button>
            <button type="submit" disabled={saveMut.isPending} className="flex-1 py-2.5 bg-compumaq-dourado text-compumaq-texto rounded-lg text-sm font-semibold hover:bg-compumaq-doradoClaro disabled:opacity-50">
              {saveMut.isPending ? 'Salvando...' : editing ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label className="block text-xs font-medium text-compumaq-textoSuave mb-1">{label}{required && <span className="text-red-500"> *</span>}</label>
      {children}
    </div>
  )
}
